// Recorte de espacios en los extremos de los textos que salen del front.
//
// Al copiar y pegar se cuelan tabuladores, saltos de línea, espacios duros (NBSP)
// y caracteres de ancho cero al principio o al final de los campos, y acaban en BD.
// `String.prototype.trim()` cubre los espacios Unicode, pero NO los caracteres de
// ancho cero (U+200B–U+200D, U+2060, U+180E), que llegan a menudo desde Word/Excel/web.
// Solo se tocan los extremos: el contenido interior (dobles espacios, saltos de
// línea de una nota multilínea) se respeta.

const EDGE_WHITESPACE = /^[\s᠎​-‍⁠]+|[\s᠎​-‍⁠]+$/g;

// Claves cuyo valor nunca se recorta: un espacio en una contraseña es parte de ella.
const PRESERVED_KEY = /pass(word)?|pwd|secret/i;

export const trimEdges = (value: string): string => value.replace(EDGE_WHITESPACE, '');

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

/**
 * Devuelve una copia de `value` con los strings recortados en profundidad (objetos
 * planos y arrays). No muta la entrada — los payloads suelen salir del estado de
 * Redux, que está congelado. Fechas, FormData, Blob, etc. se devuelven tal cual.
 */
export const deepTrimStrings = <T>(value: T, key?: string): T => {
  if (typeof value === 'string') {
    return (key !== undefined && PRESERVED_KEY.test(key) ? value : trimEdges(value)) as unknown as T;
  }
  if (Array.isArray(value)) {
    // Los elementos heredan la clave del array (`passwords: ['…']` sigue protegido).
    return value.map((item) => deepTrimStrings(item, key)) as unknown as T;
  }
  if (isPlainObject(value)) {
    const result: Record<string, unknown> = {};
    Object.keys(value).forEach((k) => {
      result[k] = deepTrimStrings(value[k], k);
    });
    return result as T;
  }
  return value;
};
